import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useLang } from "../LangContext";
import { CONTACT } from "../i18n";
import CalendarBooking from "./CalendarBooking";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
});

const Field = ({ label, children, error, testid }) => (
  <div className="border-b border-ink/20 pb-3 pt-2" data-testid={`field-${testid}`}>
    <label className="block eyebrow text-ink2 mb-2 text-[0.65rem]">{label}</label>
    {children}
    {error && (
      <p
        data-testid={`error-${testid}`}
        className="text-xs text-red-700 mt-2 italic"
      >
        {error}
      </p>
    )}
  </div>
);

const TAB_COPY = {
  sv: {
    book_slot: "Direktbokning",
    inquiry: "Skicka förfrågan",
    no_slot_warning: "Välj först datum och tid till vänster.",
    confirm_title: "Tid bokad",
    confirm_body: "Bekräftelse skickad till din e-post.",
    pay_now: "Betala direkt",
    pay_now_desc: "Slutför med kort via Stripe — bokning bekräftas vid betalning.",
    pay_later: "Boka utan betalning",
    pay_later_desc: "Vi skickar bekräftelse och fakturering hanteras separat.",
    price_label: "Pris",
    price_quoted: "Enligt offert",
    pay_unavailable_for_service:
      "Den här tjänsten har individuell prissättning — välj 'Boka utan betalning' så återkommer vi med offert.",
    pay_button: "Boka & betala",
    redirecting: "Öppnar Stripe…",
  },
  en: {
    book_slot: "Direct booking",
    inquiry: "Send enquiry",
    no_slot_warning: "Please select a date and time on the left first.",
    confirm_title: "Appointment confirmed",
    confirm_body: "A confirmation has been sent to your email.",
    pay_now: "Pay now",
    pay_now_desc: "Complete with card via Stripe — booking confirmed on payment.",
    pay_later: "Book without paying",
    pay_later_desc: "We'll send confirmation and handle invoicing separately.",
    price_label: "Price",
    price_quoted: "On quotation",
    pay_unavailable_for_service:
      "This service has individual pricing — choose 'Book without paying' and we'll send a quote.",
    pay_button: "Book & pay",
    redirecting: "Opening Stripe…",
  },
};

export default function Contact() {
  const { t, lang } = useLang();
  const tab_copy = TAB_COPY[lang] || TAB_COPY.sv;
  const initial = { name: "", email: "", phone: "", service_type: "", message: "" };
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState("book"); // "book" | "inquiry"
  const [slot, setSlot] = useState(null);
  const [payNow, setPayNow] = useState(false);
  const [priceInfo, setPriceInfo] = useState(null); // {amount, currency, payable}

  // Lookup price whenever service_type changes (only in book mode)
  useEffect(() => {
    if (!form.service_type) { setPriceInfo(null); return; }
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/payments/price`, {
          params: { service_type: form.service_type },
        });
        if (!cancel) setPriceInfo(data);
      } catch (_) {
        if (!cancel) setPriceInfo(null);
      }
    })();
    return () => { cancel = true; };
  }, [form.service_type]);

  // If user toggled pay-now but service is unpriced, force off
  useEffect(() => {
    if (payNow && priceInfo && priceInfo.payable === false) setPayNow(false);
  }, [priceInfo, payNow]);

  const setField = (k) => (e) => {
    const v = typeof e === "string" ? e : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t.contact.form.validation_required;
    if (!form.email.trim()) e.email = t.contact.form.validation_required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = t.contact.form.validation_email;
    if (!form.service_type) e.service_type = t.contact.form.validation_required;
    if (mode === "inquiry" && !form.message.trim())
      e.message = t.contact.form.validation_required;
    if (mode === "book" && !slot) e.slot = tab_copy.no_slot_warning;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (mode === "book" && payNow) {
        const res = await axios.post(`${API}/payments/checkout`, {
          ...form,
          start: slot.start,
          end: slot.end,
          language: lang,
          origin_url: window.location.origin,
        });
        if (res.data?.url) {
          // Hard navigation to Stripe — use assign so it can be cancelled by
          // the user via back button but cannot be intercepted by client-side
          // routers.
          window.location.assign(res.data.url);
          return;
        }
        throw new Error("No checkout URL");
      } else if (mode === "book") {
        const res = await axios.post(`${API}/calendar/book`, {
          ...form,
          start: slot.start,
          end: slot.end,
          language: lang,
        });
        toast.success(tab_copy.confirm_title, {
          description: res.data?.when_label || tab_copy.confirm_body,
        });
        setForm(initial);
        setSlot(null);
      } else {
        const res = await axios.post(`${API}/bookings`, {
          ...form,
          language: lang,
        });
        toast.success(t.contact.form.success_title, {
          description: res.data?.message || t.contact.form.success_body,
        });
        setForm(initial);
      }
    } catch (err) {
      toast.error(t.contact.form.error_title, {
        description: err?.response?.data?.detail?.toString() || t.contact.form.error_body,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const services = t.services.list.map((s) => s.t);

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-ink text-cream"
    >
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#FAF9F6",
            color: "#1C1917",
            border: "1px solid #E7E5E4",
            borderRadius: "0.25rem",
          },
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-y-16 md:gap-x-12">
          {/* LEFT: Title + Calendar + Address */}
          <div className="col-span-12 md:col-span-5">
            <motion.p {...fade(0)} className="eyebrow text-cream/60">
              — {t.contact.eyebrow}
            </motion.p>
            <motion.h2
              {...fade(0.05)}
              className="headline mt-6 text-4xl sm:text-5xl lg:text-6xl text-cream"
              data-testid="contact-title"
            >
              {t.contact.title}
            </motion.h2>
            <motion.p
              {...fade(0.1)}
              className="mt-6 text-base text-cream/70 leading-relaxed max-w-md"
              data-testid="contact-lede"
            >
              {t.contact.lede}
            </motion.p>

            {/* Calendar widget on dark side, in light card */}
            {mode === "book" && (
              <motion.div
                {...fade(0.15)}
                className="mt-10 bg-cream text-ink p-6 rounded-sm"
              >
                <CalendarBooking onSlotChange={setSlot} selectedSlot={slot} />
                {slot && (
                  <div
                    className="mt-6 pt-4 border-t border-line text-sm"
                    data-testid="selected-slot-summary"
                  >
                    <span className="eyebrow text-ink2">{tab_copy.confirm_title}: </span>
                    <span className="font-serif text-lg text-ink ml-1">
                      {new Date(slot.start).toLocaleDateString(lang === "en" ? "en-GB" : "sv-SE", {
                        weekday: "long", day: "numeric", month: "long",
                      })}{" "}
                      · {slot.label}
                    </span>
                  </div>
                )}
              </motion.div>
            )}

            <motion.dl
              {...fade(0.25)}
              className="mt-12 grid grid-cols-1 gap-6 max-w-md"
              data-testid="contact-info"
            >
              <div className="border-t border-cream/15 pt-4">
                <dt className="eyebrow text-cream/50 mb-2">{t.contact.address_label}</dt>
                <dd className="font-serif text-lg text-cream leading-snug">{CONTACT.address}</dd>
              </div>
              <div className="border-t border-cream/15 pt-4">
                <dt className="eyebrow text-cream/50 mb-2">{t.contact.phone_label}</dt>
                <dd>
                  <a href={`tel:${CONTACT.phone_link}`} data-testid="contact-phone-link" className="font-serif text-lg text-cream hover:text-cream/70 transition-colors">
                    {CONTACT.phone_display}
                  </a>
                </dd>
              </div>
              <div className="border-t border-cream/15 pt-4">
                <dt className="eyebrow text-cream/50 mb-2">{t.contact.email_label}</dt>
                <dd>
                  <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="font-serif text-lg text-cream hover:text-cream/70 transition-colors break-all">
                    {CONTACT.email}
                  </a>
                </dd>
              </div>
              <div className="border-t border-cream/15 pt-4">
                <dt className="eyebrow text-cream/50 mb-2">{t.contact.hours_label}</dt>
                <dd className="font-serif text-lg text-cream">{t.contact.hours}</dd>
              </div>
            </motion.dl>
          </div>

          {/* RIGHT: Form */}
          <motion.form
            {...fade(0.15)}
            onSubmit={onSubmit}
            noValidate
            data-testid="booking-form"
            className="col-span-12 md:col-span-6 md:col-start-7 bg-cream text-ink p-8 md:p-12 rounded-sm"
          >
            {/* Mode tabs */}
            <div
              className="inline-flex border border-line rounded-full p-1 mb-8 text-xs uppercase tracking-widest"
              data-testid="mode-tabs"
            >
              <button
                type="button"
                onClick={() => setMode("book")}
                data-testid="tab-book"
                className={`px-4 py-2 rounded-full transition-all ${
                  mode === "book" ? "bg-ink text-cream" : "text-ink2 hover:text-ink"
                }`}
              >
                {tab_copy.book_slot}
              </button>
              <button
                type="button"
                onClick={() => setMode("inquiry")}
                data-testid="tab-inquiry"
                className={`px-4 py-2 rounded-full transition-all ${
                  mode === "inquiry" ? "bg-ink text-cream" : "text-ink2 hover:text-ink"
                }`}
              >
                {tab_copy.inquiry}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <Field label={t.contact.form.name + " *"} error={errors.name} testid="name">
                <input
                  type="text"
                  data-testid="input-name"
                  value={form.name}
                  onChange={setField("name")}
                  className="w-full bg-transparent text-lg text-ink outline-none border-0 p-0"
                />
              </Field>
              <Field label={t.contact.form.email + " *"} error={errors.email} testid="email">
                <input
                  type="email"
                  data-testid="input-email"
                  value={form.email}
                  onChange={setField("email")}
                  className="w-full bg-transparent text-lg text-ink outline-none border-0 p-0"
                />
              </Field>
              <Field label={t.contact.form.phone} testid="phone">
                <input
                  type="tel"
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={setField("phone")}
                  className="w-full bg-transparent text-lg text-ink outline-none border-0 p-0"
                />
              </Field>
              <Field label={t.contact.form.service + " *"} error={errors.service_type} testid="service">
                <Select
                  value={form.service_type}
                  onValueChange={(v) => {
                    setForm((f) => ({ ...f, service_type: v }));
                    setErrors((er) => ({ ...er, service_type: undefined }));
                  }}
                >
                  <SelectTrigger
                    data-testid="select-service-trigger"
                    className="w-full bg-transparent border-0 p-0 h-auto text-lg text-ink hover:text-ink focus:ring-0 focus:ring-offset-0 shadow-none [&>svg]:opacity-50"
                  >
                    <SelectValue placeholder={t.contact.form.service_placeholder} />
                  </SelectTrigger>
                  <SelectContent data-testid="select-service-content">
                    {services.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`select-service-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="sm:col-span-2">
                <Field
                  label={t.contact.form.message + (mode === "inquiry" ? " *" : "")}
                  error={errors.message}
                  testid="message"
                >
                  <textarea
                    data-testid="input-message"
                    value={form.message}
                    onChange={setField("message")}
                    rows={4}
                    placeholder={t.contact.form.message_placeholder}
                    className="w-full bg-transparent text-lg text-ink outline-none border-0 p-0 resize-none placeholder:text-ink2/50"
                  />
                </Field>
              </div>
            </div>

            {mode === "book" && errors.slot && (
              <p className="mt-4 text-xs text-red-700 italic" data-testid="error-slot">
                {errors.slot}
              </p>
            )}

            {mode === "book" && slot && (
              <div className="mt-6 p-4 bg-stone50 border border-line text-sm" data-testid="form-slot-pill">
                <span className="eyebrow text-ink2">{tab_copy.confirm_title}: </span>
                <span className="font-serif text-base text-ink ml-2">
                  {new Date(slot.start).toLocaleDateString(lang === "en" ? "en-GB" : "sv-SE", {
                    weekday: "short", day: "numeric", month: "short",
                  })}{" "}
                  · {slot.label}
                </span>
              </div>
            )}

            {mode === "book" && form.service_type && priceInfo && (
              <div className="mt-6 border-t border-line pt-6" data-testid="pay-toggle">
                <p className="eyebrow text-ink2 mb-3">{tab_copy.price_label}</p>
                <div className="flex items-baseline justify-between mb-5">
                  <span className="font-serif text-3xl text-ink">
                    {priceInfo.payable
                      ? `${priceInfo.amount} ${String(priceInfo.currency).toUpperCase()}`
                      : tab_copy.price_quoted}
                  </span>
                  <span className="text-xs text-ink2 italic max-w-[60%] text-right">
                    {form.service_type}
                  </span>
                </div>
                {priceInfo.payable ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setPayNow(true)}
                      data-testid="opt-pay-now"
                      className={`text-left p-4 rounded-sm border transition-all duration-200 ${
                        payNow
                          ? "border-ink bg-ink text-cream"
                          : "border-line text-ink hover:border-ink"
                      }`}
                    >
                      <p className={`font-serif text-lg ${payNow ? "text-cream" : "text-ink"}`}>
                        {tab_copy.pay_now}
                      </p>
                      <p className={`text-xs mt-1 ${payNow ? "text-cream/70" : "text-ink2"}`}>
                        {tab_copy.pay_now_desc}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPayNow(false)}
                      data-testid="opt-pay-later"
                      className={`text-left p-4 rounded-sm border transition-all duration-200 ${
                        !payNow
                          ? "border-ink bg-ink text-cream"
                          : "border-line text-ink hover:border-ink"
                      }`}
                    >
                      <p className={`font-serif text-lg ${!payNow ? "text-cream" : "text-ink"}`}>
                        {tab_copy.pay_later}
                      </p>
                      <p className={`text-xs mt-1 ${!payNow ? "text-cream/70" : "text-ink2"}`}>
                        {tab_copy.pay_later_desc}
                      </p>
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-ink2 italic" data-testid="pay-quote-only">
                    {tab_copy.pay_unavailable_for_service}
                  </p>
                )}
              </div>
            )}

            <p className="mt-8 text-xs text-ink2 leading-relaxed">
              {t.contact.form.consent}
            </p>

            <button
              type="submit"
              disabled={submitting}
              data-testid="booking-submit"
              className="mt-8 w-full inline-flex items-center justify-center gap-3 px-8 py-5 rounded-full bg-ink text-cream text-sm font-medium hover:bg-ink2 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting
                ? (mode === "book" && payNow ? tab_copy.redirecting : t.contact.form.submitting)
                : mode === "book"
                ? (payNow ? tab_copy.pay_button + " →" : tab_copy.book_slot + " →")
                : t.contact.form.submit}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
